import type React from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useInstitutionStore } from '@/modules/auth/presentation/store/institution.store';
import { authContainer } from '@/modules/auth/di/container';
import { LoginModal } from '@/modules/auth/presentation/components/LoginModal';
import { useBlockConfig } from '@/modules/maintenance/presentation/hooks/use-block-config';
import { useEditions } from '@/modules/editions/presentation/hooks/use-editions';

interface InstitutionGuardProps {
  children: React.ReactNode;
}

export function InstitutionGuard({ children }: InstitutionGuardProps) {
  const { institution } = useParams<{ institution: string }>();
  const { setInstitution, setCourseId, setShowLoginModal, showLoginModal } =
    useInstitutionStore();

  useEffect(() => {
    if (!institution) return;
    const name = institution.toUpperCase();
    setInstitution(name);

    if (authContainer.repository.isAuthenticated(name)) {
      // sessão de um reload anterior — reidrata o courseId resolvido no login
      setCourseId(authContainer.repository.getCourseId(name));
    } else {
      setShowLoginModal(true);
    }
  }, [institution, setInstitution, setCourseId, setShowLoginModal]);

  const isAuthenticated = !showLoginModal;
  const email = institution
    ? authContainer.repository.getEmail(institution.toUpperCase())
    : null;

  const { data: blockConfig } = useBlockConfig(isAuthenticated);
  const isBlocked =
    !!blockConfig?.blocked &&
    !blockConfig.allowedEmails.some(
      (allowed) => allowed.toLowerCase() === email?.toLowerCase(),
    );

  useEditions();

  if (showLoginModal) {
    return (
      <>
        <LockedScreen />
        <LoginModal />
      </>
    );
  }

  if (isBlocked) {
    return <BlockedScreen />;
  }

  return <>{children}</>;
}

function LockedScreen() {
  return (
    <div className="flex min-h-screen select-none flex-col items-center justify-center gap-4">
      <div className="border-(--color-border) bg-(--color-bg-primary)/40 flex flex-col items-center gap-3 rounded-2xl border px-10 py-8 backdrop-blur-sm">
        <div className="bg-(--color-accent)/10 rounded-full p-3">
          <svg
            className="text-(--color-accent) h-6 w-6 opacity-60"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
            />
          </svg>
        </div>
        <p className="text-(--color-text-secondary) text-sm">
          Autenticação necessária
        </p>
      </div>
    </div>
  );
}

function BlockedScreen() {
  return (
    <div className="flex min-h-screen select-none flex-col items-center justify-center gap-4">
      <div className="border-(--color-border) bg-(--color-bg-primary)/40 flex max-w-sm flex-col items-center gap-3 rounded-2xl border px-10 py-8 text-center backdrop-blur-sm">
        <div className="bg-(--color-accent)/10 rounded-full p-3">
          <svg
            className="text-(--color-accent) h-6 w-6 opacity-60"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437 5.492-5.492"
            />
          </svg>
        </div>
        <p className="text-(--color-text-primary) text-sm font-semibold">
          Plataforma em manutenção
        </p>
        <p className="text-(--color-text-secondary) text-sm">
          Estamos realizando ajustes no sistema.
        </p>
      </div>
    </div>
  );
}
